import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ApiResponse<T> {
	status: boolean;
	data: T;
	message?: string;
}

export interface OtpSendResponse {
	message: string;
	expires_in_minutes: number;
}

export interface OtpVerifyResponse {
	token: string;
	is_existing_customer: boolean;
	customer?: {
		id: number;
		first_name: string;
		last_name: string;
		phone: string;
		email: string;
		has_had_consultation: boolean;
	};
}

export interface CalendarAvailability {
	date: string;
	day_name: string;
	is_today: boolean;
	is_past: boolean;
	is_weekly_off: boolean;
	total_slots: number;
	available_slots: number;
	booked_slots: number;
	availability_percentage: number;
	status: 'green' | 'orange' | 'gray';
}

export interface TimeSlot {
	start: string;
	end: string;
	display: string;
	available_count: number;
	total_consultants: number;
}

export interface ConsultationService {
	id: number;
	service_name: string;
	slug: string;
	tagline: string;
	description: string;
	short_description: string;
	pricing: number;
	duration_minutes: number;
	is_consultation: boolean;
	image_url: string | null;
	duration_formatted: string;
	category?: {
		id: number;
		name: string;
	};
}

export interface BookingRequest {
	token: string;
	first_name: string;
	last_name: string;
	phone: string;
	appointment_date: string;
	start_time: string;
	consultation_mode?: 'offline' | 'online';
	notes: string;
}

export interface BookingResponse {
	appointment: any;
	booking_reference: string;
}

@Injectable({
	providedIn: 'root',
})
export class BookingService {
	private http = inject(HttpClient);
	private apiUrl = environment.apiUrl;

	// OTP APIs
	sendOtp(email: string): Observable<ApiResponse<OtpSendResponse>> {
		return this.http.post<ApiResponse<OtpSendResponse>>(`${this.apiUrl}/public/otp/send`, { email });
	}

	verifyOtp(email: string, otp: string): Observable<ApiResponse<OtpVerifyResponse>> {
		return this.http.post<ApiResponse<OtpVerifyResponse>>(`${this.apiUrl}/public/otp/verify`, { email, otp });
	}

	resendOtp(email: string): Observable<ApiResponse<OtpSendResponse>> {
		return this.http.post<ApiResponse<OtpSendResponse>>(`${this.apiUrl}/public/otp/resend`, { email });
	}

	// Calendar and Slot APIs
	getCalendarAvailability(startDate: string, endDate: string): Observable<ApiResponse<CalendarAvailability[]>> {
		const params = new HttpParams().set('start_date', startDate).set('end_date', endDate);
		return this.http.get<ApiResponse<CalendarAvailability[]>>(`${this.apiUrl}/public/booking/calendar-availability`, { params });
	}

	getTimeSlots(date: string, serviceId?: number): Observable<ApiResponse<{ slots: TimeSlot[] }>> {
		let params = new HttpParams().set('date', date);
		if (serviceId) {
			params = params.set('service_id', serviceId.toString());
		}
		return this.http.get<ApiResponse<{ slots: TimeSlot[] }>>(`${this.apiUrl}/public/booking/time-slots`, { params });
	}

	getConsultationServices(): Observable<ApiResponse<ConsultationService[]>> {
		return this.http.get<ApiResponse<ConsultationService[]>>(`${this.apiUrl}/public/booking/consultation-services`);
	}

	// Booking API
	bookAppointment(data: BookingRequest): Observable<ApiResponse<BookingResponse>> {
		return this.http.post<ApiResponse<BookingResponse>>(`${this.apiUrl}/public/booking/book`, data);
	}
}
